/**
 * Web Push 購読への誘導コンポーネント
 * - 未対応 → 何も出さない
 * - iOS でホーム画面追加未済 → 案内
 * - 通知ブロック → 案内
 * - 未許可 → 「通知を受け取る」ボタン
 * - 購読済 → 「通知を停止」ボタン（軽め）
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type PushStatus, getPushStatus, subscribePush, unsubscribePush } from "../lib/push.ts";

export default function PushPrompt() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const s = await getPushStatus();
      if (!cancelled) setStatus(s);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === null || status === "unsupported") return null;

  const refresh = async () => setStatus(await getPushStatus());

  const handleSubscribe = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await subscribePush();
      if (r.subscribed) setMsg(t("push.subscribed"));
      else setMsg(r.reason === "permission-denied" ? t("push.denied") : t("common.error"));
      await refresh();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleUnsubscribe = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await unsubscribePush();
      setMsg(t("push.unsubscribed"));
      await refresh();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (status === "needs-pwa-install") {
    return (
      <aside className="nx-push-card nx-push-card--ios">
        <p className="nx-label">{t("push.label")}</p>
        <h3>{t("push.ios_install_title")}</h3>
        <p className="nx-text-secondary">{t("push.ios_install_body")}</p>
      </aside>
    );
  }

  if (status === "denied") {
    return (
      <aside className="nx-push-card nx-push-card--warn">
        <p className="nx-label">{t("push.label")}</p>
        <h3>{t("push.denied_title")}</h3>
        <p className="nx-text-secondary">{t("push.denied_body")}</p>
      </aside>
    );
  }

  if (status === "subscribed") {
    return (
      <aside className="nx-push-card">
        <p className="nx-label">{t("push.label")}</p>
        <p>{t("push.subscribed_status")}</p>
        <button type="button" onClick={handleUnsubscribe} disabled={busy} className="nx-btn-ghost">
          {t("push.unsubscribe")}
        </button>
        {msg && <p className="nx-text-secondary">{msg}</p>}
      </aside>
    );
  }

  // default
  return (
    <aside className="nx-push-card">
      <p className="nx-label">{t("push.label")}</p>
      <h3>{t("push.cta_title")}</h3>
      <p className="nx-text-secondary">{t("push.cta_body")}</p>
      <button type="button" onClick={handleSubscribe} disabled={busy} className="nx-btn-primary">
        {busy ? t("common.loading") : t("push.cta_button")}
      </button>
      {msg && <p className="nx-text-secondary">{msg}</p>}
    </aside>
  );
}
